import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

// ── Design tokens (a dashboard meglévő palettája) ────────────────────────────
const C = {
  bg: '#0f1117', card: '#1a1d27', border: '#2a2e3a', text: '#e4e6eb', dim: '#8b8fa3',
  cyan: '#00d4ff', green: '#34d399', red: '#f87171', yellow: '#fbbf24', orange: '#fb923c',
};

const LANGS = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'hu', name: 'Magyar', flag: '🇭🇺' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'sk', name: 'Slovenčina', flag: '🇸🇰' },
  { code: 'ro', name: 'Română', flag: '🇷🇴' },
  { code: 'sl', name: 'Slovenščina', flag: '🇸🇮' },
];

// Barát-ajánló promó sablon (konkrét use case) — 14 nyelven
const REFERRAL_TEMPLATE = {
  en: { title: 'Invite a friend, earn POIs! 🎁', body: 'Share AI TourMate with a friend — you both get bonus POI credits when they join. Tap to get your link!' },
  hu: { title: 'Hívj meg egy barátot, szerezz POI-t! 🎁', body: 'Oszd meg az AI TourMate-et egy baráttal — mindketten bónusz POI kreditet kaptok, ha csatlakozik. Koppints a linkért!' },
  de: { title: 'Lade einen Freund ein, sammle POIs! 🎁', body: 'Teile AI TourMate mit einem Freund — ihr erhaltet beide Bonus-POI-Guthaben, wenn er beitritt. Tippe für deinen Link!' },
  fr: { title: 'Invite un ami, gagne des POI ! 🎁', body: 'Partage AI TourMate avec un ami — vous recevez tous les deux des crédits POI bonus à son inscription. Touche pour ton lien !' },
  it: { title: 'Invita un amico, guadagna POI! 🎁', body: 'Condividi AI TourMate con un amico — ricevete entrambi crediti POI bonus quando si iscrive. Tocca per il tuo link!' },
  es: { title: '¡Invita a un amigo y gana POI! 🎁', body: 'Comparte AI TourMate con un amigo: ambos recibís créditos POI extra cuando se une. ¡Toca para tu enlace!' },
  pl: { title: 'Zaproś znajomego, zdobądź POI! 🎁', body: 'Poleć AI TourMate znajomemu — oboje dostaniecie bonusowe kredyty POI, gdy dołączy. Dotknij, aby uzyskać link!' },
  zh: { title: '邀请好友，赢取 POI！🎁', body: '把 AI TourMate 分享给好友——好友加入后你们都将获得额外 POI 积分。点按获取你的链接！' },
  ja: { title: '友達を招待してPOIをゲット！🎁', body: 'AI TourMateを友達にシェアしよう。友達が参加すると二人ともボーナスPOIがもらえます。タップしてリンクを取得！' },
  pt: { title: 'Convide um amigo, ganhe POIs! 🎁', body: 'Partilhe o AI TourMate com um amigo — ambos recebem créditos POI bónus quando ele entrar. Toque para obter o seu link!' },
  ko: { title: '친구를 초대하고 POI를 받으세요! 🎁', body: '친구에게 AI TourMate를 공유하세요. 친구가 가입하면 두 분 모두 보너스 POI 크레딧을 받습니다. 링크를 받으려면 탭하세요!' },
  sk: { title: 'Pozvi priateľa, získaj POI! 🎁', body: 'Zdieľaj AI TourMate s priateľom — obaja získate bonusové POI kredity, keď sa pripojí. Ťukni pre svoj odkaz!' },
  ro: { title: 'Invită un prieten, câștigă POI! 🎁', body: 'Distribuie AI TourMate unui prieten — primiți amândoi credite POI bonus când se înscrie. Atinge pentru linkul tău!' },
  sl: { title: 'Povabi prijatelja, prisluži POI! 🎁', body: 'Deli AI TourMate s prijateljem — oba prejmeta bonus POI kredite, ko se pridruži. Tapni za svojo povezavo!' },
};

const langName = (code) => LANGS.find(l => l.code === code)?.name || code;

// Push deep-link célpontok — a label-t az app `data.action` értékére fordítja.
// Az értékeket az app modern_home_screen.dart `_openScreenForAction()` switch-e kezeli.
const PUSH_DESTINATIONS = [
  { value: '', label: '— Nincs (csak megnyitja az appot) —' },
  { value: 'invite', label: '🎁 Meghívás / Barát-ajánló' },
  { value: 'subscribe', label: '💎 Előfizetés (Paywall)' },
  { value: 'profile', label: '👤 Profil' },
  { value: 'settings', label: '⚙️ Beállítások' },
  { value: 'achievements', label: '🏆 Eredmények' },
  { value: 'passport', label: '🛂 Város útlevél' },
  { value: 'journal', label: '📔 Felfedező napló' },
  { value: 'visited', label: '📍 Látogatott helyek' },
  { value: 'tours', label: '🧭 Túrák' },
  { value: 'restaurants', label: '🍽️ Éttermek' },
  { value: '__custom__', label: '✏️ Egyéb (egyedi action)…' },
];

function fmtDateTime(s) {
  if (!s) return '—';
  // SQLite "datetime('now')" UTC-ben tárol → kiírjuk lokál idő szerint
  const iso = s.includes('T') ? s : s.replace(' ', 'T') + 'Z';
  const d = new Date(iso);
  return isNaN(d) ? s : d.toLocaleString('hu-HU', { dateStyle: 'short', timeStyle: 'short' });
}

// ── Apró UI elemek ────────────────────────────────────────────────────────────
function Section({ step, title, hint, children, accent = C.cyan }) {
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: hint ? 4 : 14 }}>
        {step && (
          <span style={{
            width: 24, height: 24, borderRadius: 6, background: 'rgba(0,212,255,0.12)',
            color: accent, fontSize: 13, fontWeight: 700, display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>{step}</span>
        )}
        <span style={{ fontSize: 16, fontWeight: 600, color: '#fff' }}>{title}</span>
      </div>
      {hint && <div style={{ fontSize: 12.5, color: C.dim, marginBottom: 14, marginLeft: step ? 34 : 0 }}>{hint}</div>}
      {children}
    </div>
  );
}

function Pill({ active, onClick, children, color = C.cyan }) {
  return (
    <button onClick={onClick} style={{
      padding: '7px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
      border: `1px solid ${active ? color : C.border}`,
      background: active ? 'rgba(0,212,255,0.12)' : 'transparent',
      color: active ? color : C.dim, fontWeight: active ? 600 : 400,
      transition: 'all .12s',
    }}>{children}</button>
  );
}

const inputStyle = {
  width: '100%', boxSizing: 'border-box', background: C.bg, border: `1px solid ${C.border}`,
  color: C.text, padding: '10px 12px', borderRadius: 8, fontSize: 14, fontFamily: 'inherit',
};
const labelStyle = { fontSize: 12, color: C.dim, marginBottom: 6, display: 'block' };

function Field({ label, value, onChange, placeholder, max, multiline }) {
  const len = (value || '').length;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <label style={labelStyle}>{label}</label>
        {max && <span style={{ fontSize: 11, color: len > max ? C.red : C.dim }}>{len}/{max}</span>}
      </div>
      {multiline
        ? <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
            rows={3} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.4 }} />
        : <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={inputStyle} />
      }
    </div>
  );
}

function Banner({ kind, children }) {
  const map = {
    error: { bg: 'rgba(248,113,113,0.1)', bd: C.red, fg: C.red },
    success: { bg: 'rgba(52,211,153,0.1)', bd: C.green, fg: C.green },
    warn: { bg: 'rgba(251,191,36,0.1)', bd: C.yellow, fg: C.yellow },
  };
  const s = map[kind] || map.error;
  return (
    <div style={{ padding: 12, background: s.bg, border: `1px solid ${s.bd}`, borderRadius: 8, color: s.fg, marginBottom: 16, fontSize: 13.5 }}>
      {children}
    </div>
  );
}

// ── Fő komponens ────────────────────────────────────────────────────────────
export default function PushSend() {
  const [mode, setMode] = useState('universal');       // 'universal' | 'multi'
  const [universal, setUniversal] = useState({ title: '', body: '' });
  const [multi, setMulti] = useState({});               // { en: {title, body}, ... }
  const [activeLang, setActiveLang] = useState('en');

  const [targetType, setTargetType] = useState('active');
  const [platform, setPlatform] = useState('ios');
  const [activeDays, setActiveDays] = useState(30);
  const [emails, setEmails] = useState('');
  const [uids, setUids] = useState('');
  const [destChoice, setDestChoice] = useState('');   // PUSH_DESTINATIONS value vagy '__custom__'
  const [customAction, setCustomAction] = useState(''); // ha destChoice === '__custom__'

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewErr, setPreviewErr] = useState('');

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [sendErr, setSendErr] = useState('');

  const [testTarget, setTestTarget] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testErr, setTestErr] = useState('');

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  // 🆕 Push template-ek (localStorage-ben tárolva)
  const TPL_STORAGE_KEY = 'pushTemplatesV1';
  const [templates, setTemplates] = useState(() => {
    try {
      const raw = localStorage.getItem(TPL_STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [tplModalOpen, setTplModalOpen] = useState(false);
  const [tplUploadErr, setTplUploadErr] = useState('');
  const [tplUploadOk, setTplUploadOk] = useState('');

  function saveTemplatesToStorage(arr) {
    try { localStorage.setItem(TPL_STORAGE_KEY, JSON.stringify(arr)); } catch {}
    setTemplates(arr);
  }

  function handleTemplateFileUpload(e) {
    setTplUploadErr(''); setTplUploadOk('');
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const parsed = JSON.parse(ev.target.result);
        const incoming = Array.isArray(parsed) ? parsed : (parsed.templates || []);
        if (!Array.isArray(incoming) || incoming.length === 0) {
          setTplUploadErr('A JSON nem tartalmaz sablonokat.');
          return;
        }
        // Validáció
        for (const t of incoming) {
          if (!t.id || !t.name || !t.languages || typeof t.languages !== 'object') {
            setTplUploadErr(`Hibás sablon: hiányzik az id/name/languages mező (${t.name || t.id || '?'})`);
            return;
          }
        }
        // Merge: id alapján felülírja a meglévőt
        const existingMap = new Map(templates.map(t => [t.id, t]));
        let added = 0, updated = 0;
        for (const t of incoming) {
          if (existingMap.has(t.id)) updated++; else added++;
          existingMap.set(t.id, t);
        }
        const merged = Array.from(existingMap.values());
        saveTemplatesToStorage(merged);
        setTplUploadOk(`✓ ${added} új + ${updated} frissített sablon betöltve.`);
      } catch (err) {
        setTplUploadErr('Nem JSON formátum: ' + err.message);
      }
    };
    reader.readAsText(f);
    e.target.value = ''; // reset, hogy ugyanazt a fájlt újra ki lehessen választani
  }

  function removeTemplate(id) {
    saveTemplatesToStorage(templates.filter(t => t.id !== id));
  }

  function applyTemplate(tpl) {
    invalidate();
    setMode('multi');
    setMulti(JSON.parse(JSON.stringify(tpl.languages)));
    // Aktív nyelv: hu ha kitöltve, különben en, különben első
    const langs = Object.keys(tpl.languages);
    setActiveLang(langs.includes('hu') ? 'hu' : (langs.includes('en') ? 'en' : langs[0]));
    // Deep-link action (ha van)
    if (tpl.action) {
      const known = PUSH_DESTINATIONS.some(d => d.value === tpl.action);
      if (known) {
        setDestChoice(tpl.action);
        setCustomAction('');
      } else {
        setDestChoice('__custom__');
        setCustomAction(tpl.action);
      }
    } else {
      setDestChoice('');
      setCustomAction('');
    }
    setTplModalOpen(false);
  }

  const loadHistory = useCallback(() => {
    setHistoryLoading(true);
    api.pushSend.history(50).then(setHistory).catch(() => {}).finally(() => setHistoryLoading(false));
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  // A célzás / tartalom változásakor a korábbi előnézet érvénytelen
  const invalidate = () => { setPreview(null); setSendResult(null); setSendErr(''); };

  const setMultiField = (lang, key, val) => {
    invalidate();
    setMulti(m => ({ ...m, [lang]: { ...(m[lang] || { title: '', body: '' }), [key]: val } }));
  };

  const filledLangs = LANGS.filter(l => multi[l.code]?.title && multi[l.code]?.body).map(l => l.code);

  // ── Payload építés ───────────────────────────────────────────────────────
  function buildContent() {
    if (mode === 'universal') return { title: universal.title.trim(), body: universal.body.trim() };
    const out = {};
    for (const code of filledLangs) out[code] = { title: multi[code].title.trim(), body: multi[code].body.trim() };
    return out;
  }
  function buildTargeting() {
    switch (targetType) {
      case 'platform': return { type: 'platform', platform };
      case 'active': return { type: 'active', days: Number(activeDays) || 30 };
      case 'hasBalance': return { type: 'hasBalance' };
      case 'noBalance': return { type: 'noBalance' };
      case 'specific': return {
        type: 'specific',
        emails: emails.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean),
        uids: uids.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean),
      };
      default: return { type: 'all' };
    }
  }
  // A ténylegesen küldött action érték (legördülő vagy egyedi)
  const actionValue = (destChoice === '__custom__' ? customAction : destChoice).trim();

  function buildPayload() {
    const p = { mode, content: buildContent(), targeting: buildTargeting() };
    if (actionValue) p.data = { action: actionValue };
    return p;
  }

  // Tartalom validáció (UI gating)
  const contentValid = mode === 'universal'
    ? (universal.title.trim() && universal.body.trim())
    : filledLangs.length > 0;
  const targetingValid = targetType !== 'specific' ||
    emails.trim() || uids.trim();

  // ── Akciók ──────────────────────────────────────────────────────────────
  const doPreview = () => {
    setPreviewErr(''); setSendResult(null); setSendErr('');
    setPreviewLoading(true);
    api.pushSend.preview(buildPayload())
      .then(setPreview)
      .catch(e => setPreviewErr(e.message || 'Hiba'))
      .finally(() => setPreviewLoading(false));
  };

  const doSend = () => {
    setConfirmOpen(false);
    setSendErr(''); setSending(true);
    api.pushSend.send({ ...buildPayload(), confirm: true })
      .then(r => { setSendResult(r); setPreview(null); loadHistory(); })
      .catch(e => setSendErr(e.message || 'Hiba'))
      .finally(() => setSending(false));
  };

  const doTest = () => {
    setTestErr(''); setTestResult(null); setTesting(true);
    const t = testTarget.trim();
    const payload = { mode, content: buildContent() };
    if (actionValue) payload.data = { action: actionValue };
    if (t.includes('@')) payload.email = t;
    else if (t) payload.uid = t;
    // ha üres → backend a FIREBASE_ADMIN_UID-ra küld
    api.pushSend.test(payload)
      .then(setTestResult)
      .catch(e => setTestErr(e.message || 'Hiba'))
      .finally(() => setTesting(false));
  };

  const loadTemplate = () => {
    invalidate();
    setMode('multi');
    setMulti(JSON.parse(JSON.stringify(REFERRAL_TEMPLATE)));
    setActiveLang('hu');
    setTargetType('active');
    setActiveDays(30);
    setDestChoice('invite');
    setCustomAction('');
  };

  const clearAll = () => {
    invalidate();
    setUniversal({ title: '', body: '' });
    setMulti({});
    setDestChoice('');
    setCustomAction('');
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ maxWidth: 980 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ margin: 0, fontSize: 24, color: '#fff' }}>🔔 Push küldés</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setTplModalOpen(true)} style={{
            padding: '7px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
            border: `1px solid ${C.green}`, background: 'rgba(52,211,153,0.1)', color: C.green, fontWeight: 600,
          }}>📋 Sablonok {templates.length > 0 && <span style={{ marginLeft: 4, opacity: 0.7 }}>({templates.length})</span>}</button>
          <button onClick={clearAll} style={{
            padding: '7px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
            border: `1px solid ${C.border}`, background: 'transparent', color: C.dim,
          }}>Ürítés</button>
        </div>
      </div>
      <div style={{ color: C.dim, fontSize: 13, marginBottom: 22 }}>
        Célzott FCM értesítés küldése a felhasználóknak. Az érvénytelen tokeneket a rendszer automatikusan törli.
      </div>

      {/* 🆕 Sablon-választó modal */}
      {tplModalOpen && (
        <div onClick={() => setTplModalOpen(false)} style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)', zIndex: 100, display: 'flex',
          alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
            padding: 24, maxWidth: 700, width: '100%', maxHeight: '85vh', overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ margin: 0, fontSize: 18, color: '#fff' }}>📋 Push sablonok</h2>
              <button onClick={() => setTplModalOpen(false)} style={{
                background: 'transparent', border: 'none', color: C.dim, fontSize: 22, cursor: 'pointer', padding: 0,
              }}>✕</button>
            </div>

            {/* File upload */}
            <div style={{ background: C.bg, border: `1px dashed ${C.border}`, borderRadius: 8, padding: 14, marginBottom: 16 }}>
              <label style={{ display: 'block', cursor: 'pointer' }}>
                <div style={{ fontSize: 13, color: C.text, marginBottom: 8 }}>
                  📥 <strong>JSON sablon-fájl betöltése</strong> <span style={{ color: C.dim, fontSize: 12 }}>(merge: új ID hozzáadva, meglévő ID frissítve)</span>
                </div>
                <input type="file" accept=".json,application/json" onChange={handleTemplateFileUpload} style={{
                  display: 'block', width: '100%', fontSize: 13, color: C.dim,
                  background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: 6,
                }} />
              </label>
              {tplUploadErr && <div style={{ marginTop: 8, color: C.red, fontSize: 12 }}>{tplUploadErr}</div>}
              {tplUploadOk && <div style={{ marginTop: 8, color: C.green, fontSize: 12 }}>{tplUploadOk}</div>}
            </div>

            {/* Sablon lista */}
            {templates.length === 0 ? (
              <div style={{ padding: '24px 16px', textAlign: 'center', color: C.dim, fontSize: 13 }}>
                Még nincs sablon. Tölts be egy JSON fájlt a fenti gombbal.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {templates.map(tpl => {
                  const langCount = Object.keys(tpl.languages || {}).length;
                  const huPreview = tpl.languages?.hu?.title || tpl.languages?.en?.title || '(nincs előnézet)';
                  return (
                    <div key={tpl.id} style={{
                      background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: 12,
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, color: C.text, fontWeight: 600, marginBottom: 4 }}>{tpl.name}</div>
                        <div style={{ fontSize: 12, color: C.dim, marginBottom: 4 }}>
                          {tpl.category && <span>{tpl.category} · </span>}
                          {langCount} nyelv
                          {tpl.action && <span> · 🔗 {tpl.action}</span>}
                        </div>
                        <div style={{ fontSize: 12, color: C.dim, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          "{huPreview}"
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <button onClick={() => applyTemplate(tpl)} style={{
                          padding: '6px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                          border: `1px solid ${C.cyan}`, background: 'rgba(0,212,255,0.12)', color: C.cyan, fontWeight: 600,
                        }}>Betöltés</button>
                        <button onClick={() => { if (window.confirm(`Törölöd: "${tpl.name}"?`)) removeTemplate(tpl.id); }} style={{
                          padding: '6px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                          border: `1px solid ${C.border}`, background: 'transparent', color: C.dim,
                        }}>🗑</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ marginTop: 16, fontSize: 11, color: C.dim, textAlign: 'center' }}>
              A sablonok a böngésződ localStorage-jében tárolódnak. Export: tarolt sablonok átvitelét másik gépre a JSON fájl scp-elésével végezheted.
            </div>
          </div>
        </div>
      )}

      {/* 1. ÜZENET */}
      <Section step="1" title="Üzenet">
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <Pill active={mode === 'universal'} onClick={() => { invalidate(); setMode('universal'); }}>Univerzális</Pill>
          <Pill active={mode === 'multi'} onClick={() => { invalidate(); setMode('multi'); }}>Több nyelvű (14)</Pill>
        </div>

        {mode === 'universal' ? (
          <>
            <Field label="Cím (title)" value={universal.title} max={200}
              placeholder="pl. Új funkció érkezett!"
              onChange={v => { invalidate(); setUniversal(u => ({ ...u, title: v })); }} />
            <Field label="Szöveg (body)" value={universal.body} max={1000} multiline
              placeholder="Az értesítés törzse…"
              onChange={v => { invalidate(); setUniversal(u => ({ ...u, body: v })); }} />
          </>
        ) : (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
              {LANGS.map(l => {
                const isFilled = filledLangs.includes(l.code);
                const isActive = activeLang === l.code;
                return (
                  <button key={l.code} onClick={() => setActiveLang(l.code)} title={l.name} style={{
                    padding: '6px 10px', borderRadius: 7, fontSize: 12.5, cursor: 'pointer',
                    border: `1px solid ${isActive ? C.cyan : C.border}`,
                    background: isActive ? 'rgba(0,212,255,0.12)' : 'transparent',
                    color: isActive ? C.cyan : (isFilled ? C.text : C.dim),
                    display: 'flex', alignItems: 'center', gap: 5, fontWeight: isActive ? 600 : 400,
                  }}>
                    <span>{l.flag}</span>{l.code}
                    {isFilled && <span style={{ color: C.green, fontSize: 11 }}>✓</span>}
                  </button>
                );
              })}
            </div>
            <div style={{ fontSize: 12, color: C.dim, marginBottom: 12 }}>
              Szerkesztett nyelv: <strong style={{ color: C.text }}>{langName(activeLang)}</strong>
              {' · '}Kitöltve: <strong style={{ color: filledLangs.length ? C.green : C.dim }}>{filledLangs.length}/14</strong>
              {' · '}A nem kitöltött nyelvek a felhasználónál angolra (en) esnek vissza.
            </div>
            <Field label={`Cím (title) — ${activeLang}`} max={200}
              value={multi[activeLang]?.title || ''}
              placeholder="pl. New feature available!"
              onChange={v => setMultiField(activeLang, 'title', v)} />
            <Field label={`Szöveg (body) — ${activeLang}`} max={1000} multiline
              value={multi[activeLang]?.body || ''}
              placeholder="Notification body…"
              onChange={v => setMultiField(activeLang, 'body', v)} />
          </>
        )}
      </Section>

      {/* 2. CÉLZÁS */}
      <Section step="2" title="Célzás">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
          <Pill active={targetType === 'all'} onClick={() => { invalidate(); setTargetType('all'); }}>Összes user</Pill>
          <Pill active={targetType === 'platform'} onClick={() => { invalidate(); setTargetType('platform'); }}>Platform szerint</Pill>
          <Pill active={targetType === 'active'} onClick={() => { invalidate(); setTargetType('active'); }}>Aktív userek</Pill>
          <Pill active={targetType === 'hasBalance'} onClick={() => { invalidate(); setTargetType('hasBalance'); }}>Van POI egyenleg</Pill>
          <Pill active={targetType === 'noBalance'} onClick={() => { invalidate(); setTargetType('noBalance'); }}>Nincs POI egyenleg</Pill>
          <Pill active={targetType === 'specific'} onClick={() => { invalidate(); setTargetType('specific'); }}>Egyedi (email/UID)</Pill>
        </div>

        {targetType === 'platform' && (
          <div style={{ display: 'flex', gap: 8 }}>
            <Pill active={platform === 'ios'} onClick={() => { invalidate(); setPlatform('ios'); }}>🍎 iOS</Pill>
            <Pill active={platform === 'android'} onClick={() => { invalidate(); setPlatform('android'); }}>🤖 Android</Pill>
          </div>
        )}
        {targetType === 'active' && (
          <div style={{ maxWidth: 240 }}>
            <label style={labelStyle}>Aktív az utolsó hány napban? (lastPositionAt)</label>
            <div style={{ display: 'flex', gap: 6 }}>
              {[7, 14, 30, 90].map(d => (
                <Pill key={d} active={Number(activeDays) === d} onClick={() => { invalidate(); setActiveDays(d); }}>{d} nap</Pill>
              ))}
            </div>
            <input type="number" min="1" max="365" value={activeDays}
              onChange={e => { invalidate(); setActiveDays(e.target.value); }}
              style={{ ...inputStyle, marginTop: 8, maxWidth: 120 }} />
          </div>
        )}
        {targetType === 'specific' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
            <div>
              <label style={labelStyle}>Email címek (vesszővel / sortöréssel)</label>
              <textarea value={emails} onChange={e => { invalidate(); setEmails(e.target.value); }} rows={3}
                placeholder="user1@example.com, user2@example.com" style={{ ...inputStyle, resize: 'vertical' }} />
            </div>
            <div>
              <label style={labelStyle}>UID-k (vesszővel / sortöréssel)</label>
              <textarea value={uids} onChange={e => { invalidate(); setUids(e.target.value); }} rows={3}
                placeholder="abc123…, def456…" style={{ ...inputStyle, resize: 'vertical' }} />
            </div>
          </div>
        )}

        <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${C.border}`, maxWidth: 380 }}>
          <label style={labelStyle}>Koppintásra ide ugorjon (deep-link célpont)</label>
          <select value={destChoice} onChange={e => { invalidate(); setDestChoice(e.target.value); }} style={inputStyle}>
            {PUSH_DESTINATIONS.map(d => (
              <option key={d.value || 'none'} value={d.value}>{d.label}</option>
            ))}
          </select>
          {destChoice === '__custom__' && (
            <input value={customAction} onChange={e => { invalidate(); setCustomAction(e.target.value); }}
              placeholder="egyedi action (pl. promo_summer) — az appnak kezelnie kell"
              style={{ ...inputStyle, marginTop: 8 }} />
          )}
          <div style={{ fontSize: 11.5, color: C.dim, marginTop: 8 }}>
            Az app a <code>data.action</code> mező alapján navigál a kiválasztott képernyőre. A „Nincs" opciónál
            koppintásra csak megnyílik az app. Új célpont az app <code>_openScreenForAction()</code> switch-ébe vehető fel.
          </div>
        </div>
      </Section>

      {/* 3. ELŐNÉZET */}
      <Section step="3" title="Előnézet">
        <button onClick={doPreview} disabled={!contentValid || !targetingValid || previewLoading} style={{
          padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600,
          cursor: (!contentValid || !targetingValid || previewLoading) ? 'not-allowed' : 'pointer',
          border: 'none', background: (!contentValid || !targetingValid) ? '#2a2e3a' : C.cyan,
          color: (!contentValid || !targetingValid) ? C.dim : '#06121a',
        }}>
          {previewLoading ? 'Számolás…' : '🔍 Előnézet / hány usert érint'}
        </button>
        {!contentValid && <span style={{ color: C.dim, fontSize: 12.5, marginLeft: 12 }}>Tölts ki üzenetet a folytatáshoz.</span>}

        {previewErr && <div style={{ marginTop: 14 }}><Banner kind="error">{previewErr}</Banner></div>}

        {preview && (
          <div style={{ marginTop: 18 }}>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 38, fontWeight: 800, color: C.cyan, lineHeight: 1 }}>
                  {preview.recipientCount.toLocaleString('hu-HU')}
                </div>
                <div style={{ fontSize: 12.5, color: C.dim, marginTop: 4 }}>címzett (van FCM token)</div>
              </div>
              <div style={{ height: 44, width: 1, background: C.border }} />
              <div style={{ fontSize: 12.5, color: C.dim, lineHeight: 1.7 }}>
                <div><strong style={{ color: C.text }}>{preview.targetingSummary}</strong></div>
                <div>Lekért userek: {preview.stats.scanned.toLocaleString('hu-HU')} · Token nélkül: {preview.stats.withoutToken.toLocaleString('hu-HU')}</div>
                <div>
                  🍎 iOS: {preview.stats.byPlatform.ios || 0} · 🤖 Android: {preview.stats.byPlatform.android || 0}
                  {preview.stats.byPlatform.unknown ? ` · ❓ Egyéb: ${preview.stats.byPlatform.unknown}` : ''}
                </div>
              </div>
            </div>

            {/* Nyelvi eloszlás */}
            {Object.keys(preview.stats.byLang || {}).length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                {Object.entries(preview.stats.byLang).sort((a, b) => b[1] - a[1]).map(([lang, n]) => (
                  <span key={lang} style={{
                    fontSize: 12, color: C.dim, background: C.bg, border: `1px solid ${C.border}`,
                    borderRadius: 6, padding: '4px 9px',
                  }}>{lang} · <strong style={{ color: C.text }}>{n}</strong></span>
                ))}
              </div>
            )}

            {/* Minta üzenet */}
            <div style={{ fontSize: 12, color: C.dim, marginBottom: 8 }}>Minta értesítés:</div>
            {preview.sample.type === 'universal' ? (
              <NotifPreview title={preview.sample.message.title} body={preview.sample.message.body} />
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
                {preview.sample.perLang.map(p => (
                  <div key={p.lang}>
                    <div style={{ fontSize: 11.5, color: C.dim, marginBottom: 4 }}>
                      {p.lang} · {p.users} user
                    </div>
                    <NotifPreview title={p.message.title} body={p.message.body} />
                  </div>
                ))}
              </div>
            )}

            {preview.recipientCount === 0 && (
              <div style={{ marginTop: 14 }}><Banner kind="warn">Ez a célzás 0 címzettet talált — nincs kinek küldeni.</Banner></div>
            )}
          </div>
        )}
      </Section>

      {/* 4. KÜLDÉS */}
      <Section step="4" title="Küldés" accent={C.green}>
        {/* Teszt küldés — élesítés előtt */}
        <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: 14, marginBottom: 18 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#fff', marginBottom: 4 }}>🧪 Teszt küldés</div>
          <div style={{ fontSize: 12, color: C.dim, marginBottom: 10 }}>
            Az aktuális üzenetet egyetlen címzettnek küldi el (a célzás nem számít). Nem kerül a történetbe.
            Hagyd üresen, hogy az admin UID-ra (FIREBASE_ADMIN_UID) menjen.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input value={testTarget} onChange={e => { setTestResult(null); setTestErr(''); setTestTarget(e.target.value); }}
              placeholder="email vagy UID (opcionális)" style={{ ...inputStyle, maxWidth: 320, flex: '1 1 240px' }} />
            <button onClick={doTest} disabled={!contentValid || testing} style={{
              padding: '10px 18px', borderRadius: 8, fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap',
              cursor: (!contentValid || testing) ? 'not-allowed' : 'pointer', border: `1px solid ${C.cyan}`,
              background: contentValid ? 'rgba(0,212,255,0.12)' : 'transparent',
              color: contentValid ? C.cyan : C.dim,
            }}>{testing ? 'Küldés…' : '🧪 Teszt küldése'}</button>
          </div>
          {!contentValid && <div style={{ fontSize: 12, color: C.dim, marginTop: 8 }}>Előbb tölts ki egy üzenetet.</div>}
          {testErr && <div style={{ marginTop: 10 }}><Banner kind="error">{testErr}</Banner></div>}
          {testResult && (
            <div style={{ marginTop: 10 }}>
              <Banner kind={testResult.success > 0 ? 'success' : 'warn'}>
                {testResult.warning
                  ? testResult.warning
                  : <>🧪 Teszt elküldve — <strong>{testResult.success}</strong> sikeres, <strong>{testResult.failed}</strong> sikertelen
                      {testResult.target?.uid ? <> (UID: {testResult.target.uid})</> : ''}
                      {testResult.invalidRemoved > 0 ? <> · token törölve</> : ''}.</>}
              </Banner>
            </div>
          )}
        </div>

        {/* Éles küldés */}
        {sendErr && <Banner kind="error">{sendErr}</Banner>}
        {sendResult && (
          <Banner kind={sendResult.success > 0 ? 'success' : 'warn'}>
            {sendResult.warning
              ? sendResult.warning
              : <>✅ Kész — <strong>{sendResult.success}</strong> sikeres, <strong>{sendResult.failed}</strong> sikertelen
                  {' '}({sendResult.sent} címzettből){sendResult.invalidRemoved > 0
                    ? <> · 🧹 {sendResult.invalidRemoved} érvénytelen token törölve</> : ''}.</>}
          </Banner>
        )}

        <button
          onClick={() => setConfirmOpen(true)}
          disabled={!preview || preview.recipientCount === 0 || sending}
          style={{
            padding: '12px 26px', borderRadius: 8, fontSize: 15, fontWeight: 700,
            cursor: (!preview || preview.recipientCount === 0 || sending) ? 'not-allowed' : 'pointer',
            border: 'none',
            background: (!preview || preview.recipientCount === 0) ? '#2a2e3a' : C.green,
            color: (!preview || preview.recipientCount === 0) ? C.dim : '#04140c',
          }}>
          {sending ? 'Küldés folyamatban…' : '📤 Push küldése'}
        </button>
        {!preview && <span style={{ color: C.dim, fontSize: 12.5, marginLeft: 12 }}>Először futtass előnézetet.</span>}
      </Section>

      {/* TÖRTÉNET */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <span style={{ fontSize: 16, fontWeight: 600, color: '#fff' }}>📜 Történet</span>
          <button onClick={loadHistory} style={{
            padding: '5px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
            border: `1px solid ${C.border}`, background: 'transparent', color: C.dim,
          }}>↻ Frissítés</button>
        </div>
        {historyLoading ? (
          <div style={{ color: C.dim, fontSize: 13 }}>Betöltés…</div>
        ) : history.length === 0 ? (
          <div style={{ color: C.dim, fontSize: 13 }}>Még nincs elküldött push.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}`, color: C.dim }}>
                  <th style={{ padding: '8px 10px', textAlign: 'left' }}>Időpont</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left' }}>Üzenet</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left' }}>Célzás</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center' }}>Mód</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right' }}>Címzett</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right' }}>Sikeres</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right' }}>Sikertelen</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right' }}>Törölt token</th>
                </tr>
              </thead>
              <tbody>
                {history.map(h => (
                  <tr key={h.id} style={{ borderBottom: `1px solid ${C.border}`, color: C.text }}>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{fmtDateTime(h.sent_at)}</td>
                    <td style={{ padding: '8px 10px', maxWidth: 240 }}>
                      <div style={{ color: '#fff', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.title || '—'}</div>
                      <div style={{ color: C.dim, fontSize: 11.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.body}</div>
                      {h.error && <div style={{ color: C.red, fontSize: 11.5 }}>⚠ {h.error}</div>}
                    </td>
                    <td style={{ padding: '8px 10px', color: C.dim }}>{h.targeting_summary}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'center', color: C.dim }}>{h.mode === 'multi' ? 'Több nyelv' : 'Univ.'}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', color: C.dim }}>{h.sent_count}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', color: C.green, fontWeight: 600 }}>{h.success_count}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', color: h.fail_count > 0 ? C.red : C.dim }}>{h.fail_count}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', color: h.invalid_removed > 0 ? C.orange : C.dim }}>{h.invalid_removed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* MEGERŐSÍTŐ MODAL */}
      {confirmOpen && preview && (
        <div onClick={() => setConfirmOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
          <div onClick={e => e.stopPropagation()} className="card" style={{ maxWidth: 440, width: '100%' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: 10 }}>Push küldés megerősítése</div>
            <div style={{ fontSize: 14, color: C.text, lineHeight: 1.6, marginBottom: 18 }}>
              Az értesítés <strong style={{ color: C.cyan }}>{preview.recipientCount.toLocaleString('hu-HU')}</strong> felhasználónak megy ki.<br />
              Célzás: <strong>{preview.targetingSummary}</strong>.<br />
              Ez a művelet nem visszavonható.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmOpen(false)} style={{
                padding: '9px 18px', borderRadius: 8, fontSize: 14, cursor: 'pointer',
                border: `1px solid ${C.border}`, background: 'transparent', color: C.dim,
              }}>Mégse</button>
              <button onClick={doSend} style={{
                padding: '9px 18px', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer',
                border: 'none', background: C.green, color: '#04140c',
              }}>Igen, küldés</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// iOS/Android-szerű értesítés előnézet kártya
function NotifPreview({ title, body }) {
  return (
    <div style={{
      background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12,
      display: 'flex', gap: 10, alignItems: 'flex-start',
    }}>
      <div style={{
        width: 34, height: 34, borderRadius: 8, background: 'linear-gradient(135deg,#00d4ff,#34d399)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0,
      }}>🗺️</div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: '#fff', wordBreak: 'break-word' }}>{title || <span style={{ color: C.dim, fontWeight: 400 }}>(nincs cím)</span>}</div>
        <div style={{ fontSize: 12.5, color: C.text, marginTop: 2, wordBreak: 'break-word', lineHeight: 1.4 }}>{body || <span style={{ color: C.dim }}>(nincs szöveg)</span>}</div>
        <div style={{ fontSize: 10.5, color: C.dim, marginTop: 4 }}>AI TourMate · most</div>
      </div>
    </div>
  );
}
